import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import {
  Users, GraduationCap, Megaphone, CalendarDays,
  ClipboardCheck, Images, School, Wallet, QrCode, Tag, FileText, UserCog,
  Store, Package, Image as ImageIcon, Settings, LayoutGrid, Plus, ChevronRight,
  BookOpen,
} from "lucide-react";
import { canAccess, useUserRole } from "@/hooks/use-user-role";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { setCachedStateValue, useCachedState } from "@/hooks/use-cached-state";

type Item = {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string; // tailwind classes: bg + text
  subtitle?: string;
};
type Announcement = { id: string; title: string; content: string; date: string };
type CachedStudent = {
  id: string;
  nisn: string | null;
  full_name: string;
  class_id: string | null;
  gender: "L" | "P" | null;
  birth_date: string | null;
  address: string | null;
  parent_name: string | null;
  father_name: string | null;
  mother_name: string | null;
  parent_phone: string | null;
  email: string | null;
  photo_url: string | null;
};
type CachedClass = { id: string; name: string };

const GROUPS: { group: string; items: Item[] }[] = [

  { group: "Akademik", items: [
    { title: "Jadwal", url: "/schedules", icon: CalendarDays, color: "bg-emerald-100 text-emerald-600", subtitle: "Pelajaran" },
    { title: "Absensi", url: "/attendance", icon: ClipboardCheck, color: "bg-sky-100 text-sky-600", subtitle: "Kehadiran" },
    { title: "Pengumuman", url: "/announcements", icon: Megaphone, color: "bg-rose-100 text-rose-600", subtitle: "Madrasah" },
    { title: "Pengumuman Staf", url: "/staff-announcements", icon: Megaphone, color: "bg-amber-100 text-amber-600", subtitle: "Internal Staf" },
    { title: "Galeri", url: "/gallery", icon: Images, color: "bg-cyan-100 text-cyan-600", subtitle: "Kegiatan" },
  ]},
  { group: "Data Master", items: [
    { title: "Data Siswa", url: "/students", icon: Users, color: "bg-violet-100 text-violet-600", subtitle: "Profil Siswa" },
    { title: "Data Guru", url: "/teachers", icon: GraduationCap, color: "bg-purple-100 text-purple-600", subtitle: "Profil Pendidik" },
    { title: "Profil Sekolah", url: "/school", icon: School, color: "bg-teal-100 text-teal-600", subtitle: "Profil MI" },
    { title: "Anggota & Akses", url: "/members", icon: UserCog, color: "bg-amber-100 text-amber-600", subtitle: "Hak Akses" },
  ]},
  { group: "Keuangan", items: [
    { title: "Jenis Pembayaran", url: "/payment-types", icon: Tag, color: "bg-emerald-100 text-emerald-600", subtitle: "Kategori Tagihan" },
    { title: "Tagihan Siswa", url: "/student-bills", icon: FileText, color: "bg-sky-100 text-sky-600", subtitle: "Daftar Tagihan" },
    { title: "Verifikasi Pembayaran", url: "/payments", icon: Wallet, color: "bg-orange-100 text-orange-600", subtitle: "SPP Online" },
    { title: "Pengaturan Bayar", url: "/payment-settings", icon: QrCode, color: "bg-slate-100 text-slate-600", subtitle: "QRIS & Bank" },
  ]},
  { group: "Toko Sekolah", items: [
    { title: "Toko Sekolah", url: "/store", icon: Store, color: "bg-teal-100 text-teal-600", subtitle: "Dashboard" },
    { title: "Kategori", url: "/store/categories", icon: LayoutGrid, color: "bg-purple-100 text-purple-600", subtitle: "Produk" },
    { title: "Produk", url: "/store/products", icon: Package, color: "bg-orange-100 text-orange-600", subtitle: "Katalog" },
    { title: "Banner", url: "/store/banners", icon: ImageIcon, color: "bg-rose-100 text-rose-600", subtitle: "Promosi" },
    { title: "Pengaturan Store", url: "/store/settings", icon: Settings, color: "bg-slate-100 text-slate-600", subtitle: "Konfigurasi" },
  ]},
];

type Stats = { students: number; teachers: number; classes: number; announcements: number };

export function MobileHomeMenu() {
  const { role, isAdmin } = useUserRole();
  const sourceTable = isAdmin ? "announcements" : "staff_announcements";
  const targetUrl = isAdmin ? "/announcements" : "/staff-announcements";
  const cardTitle = isAdmin ? "Pengumuman Terbaru" : "Pengumuman Khusus Staf";
  const [anns, setAnns] = useCachedState<Announcement[]>(`mobile-home:${sourceTable}:anns`, []);
  const [stats, setStats] = useCachedState<Stats>(`mobile-home:${sourceTable}:stats`, { students: 0, teachers: 0, classes: 0, announcements: 0 });

  useEffect(() => {
    if (!role) return;
    let cancelled = false;
    const load = async () => {
      const [a, b, c, d] = await Promise.all([
        supabase.from("students").select("*", { count: "exact", head: true }),
        supabase.from("teachers").select("*", { count: "exact", head: true }),
        supabase.from("classes").select("*", { count: "exact", head: true }),
        supabase.from(sourceTable).select("*", { count: "exact", head: true }),
      ]);
      if (cancelled) return;
      setStats({
        students: a.count ?? 0,
        teachers: b.count ?? 0,
        classes: c.count ?? 0,
        announcements: d.count ?? 0,
      });
    };
    load();
    return () => { cancelled = true; };
  }, [role, sourceTable]);

  useEffect(() => {
    if (!role) return;
    let cancelled = false;
    const warm = async () => {
      const jobs: PromiseLike<unknown>[] = [];
      if (canAccess(role, "/students")) {
        jobs.push(
          supabase.from("students").select("*").order("full_name").then(({ data, error }) => {
            if (!cancelled && !error && data) setCachedStateValue("students:rows", data as CachedStudent[]);
          }),
          supabase.from("classes").select("id,name").order("name").then(({ data, error }) => {
            if (!cancelled && !error && data) setCachedStateValue("students:classes", data as CachedClass[]);
          })
        );
      }
      if (canAccess(role, "/announcements")) {
        jobs.push(
          supabase
            .from("announcements")
            .select("*")
            .order("date", { ascending: false })
            .order("created_at", { ascending: false })
            .then(({ data, error }) => {
              if (!cancelled && !error && data) setCachedStateValue("announcements:rows", data);
            })
        );
      }
      if (canAccess(role, "/staff-announcements")) {
        jobs.push(
          supabase
            .from("staff_announcements")
            .select("*")
            .order("date", { ascending: false })
            .order("created_at", { ascending: false })
            .then(({ data, error }) => {
              if (!cancelled && !error && data) setCachedStateValue("staff-announcements:rows", data);
            })
        );
      }
      await Promise.allSettled(jobs);
    };
    warm();
    return () => { cancelled = true; };
  }, [role]);

  useEffect(() => {
    if (!role) return;
    let cancelled = false;
    const load = async () => {
      const { data } = await supabase
        .from(sourceTable)
        .select("id, title, content, date")
        .order("date", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(1);
      if (!cancelled) setAnns((data ?? []) as Announcement[]);
    };
    load();
    const ch = supabase
      .channel(`mobile-home-${sourceTable}`)
      .on("postgres_changes", { event: "*", schema: "public", table: sourceTable }, load)
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(ch); };
  }, [role, sourceTable]);

  if (!role) return null;

  const canCreate = isAdmin;

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });

  const statCards = [
    { label: "Total Siswa", value: stats.students, icon: Users, color: "bg-emerald-100 text-emerald-600" },
    { label: "Guru & Staff", value: stats.teachers, icon: GraduationCap, color: "bg-sky-100 text-sky-600" },
    { label: "Kelas", value: stats.classes, icon: BookOpen, color: "bg-indigo-100 text-indigo-600" },
    { label: "Pengumuman", value: stats.announcements, icon: Megaphone, color: "bg-amber-100 text-amber-600" },
  ];

  const allItems = GROUPS.flatMap((g) => g.items).filter((i) => canAccess(role, i.url));

  return (
    <div className="md:hidden -mx-4 sm:-mx-6 -mt-4 sm:-mt-6">
      {/* Pengumuman — overlap curved header */}
      <div className="px-4 sm:px-6 mt-4 relative z-10">
        <div className="rounded-2xl bg-card border border-border/60 shadow-[var(--shadow-elevation-1)] overflow-hidden">
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h3 className="font-bold text-[15px]">{cardTitle}</h3>
            <div className="flex items-center gap-1">
              {canCreate && (
                <Link
                  to={targetUrl as "/announcements"}
                  className="inline-flex items-center gap-1 text-[12px] font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-full active:scale-95 transition"
                >
                  <Plus className="w-3.5 h-3.5" /> Buat
                </Link>
              )}
              <Link
                to={targetUrl as "/announcements"}
                className="text-[12px] font-semibold text-primary px-2 py-1 active:scale-95 transition"
              >
                Lihat Semua
              </Link>
            </div>
          </div>

          <div className="divide-y divide-border/60">
            {anns.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                Belum ada pengumuman
              </div>
            ) : (
              anns.map((a) => (
                <Link
                  key={a.id}
                  to={targetUrl as "/announcements"}
                  className="flex items-start gap-3 px-4 py-3 active:bg-accent/40 transition"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Megaphone className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[14px] leading-tight truncate">
                      {a.title}
                    </div>
                    <div className="text-[12px] text-muted-foreground line-clamp-2 mt-0.5">
                      {a.content}
                    </div>
                    <div className="text-[11px] text-muted-foreground/80 mt-1">
                      {fmtDate(a.date)}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
                </Link>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Stats cards */}
      <div className="px-4 sm:px-6 mt-3 relative z-10">
        <div className="grid grid-cols-4 gap-2">
          {statCards.map((s) => (
            <div
              key={s.label}
              className="rounded-xl bg-card border border-border/60 shadow-[var(--shadow-elevation-1)] px-2 py-2.5 flex flex-col items-center text-center gap-1"
            >
              <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", s.color)}>
                <s.icon className="w-4 h-4" />
              </div>
              <div className="font-bold text-[16px] leading-none mt-0.5">{s.value}</div>
              <div className="text-[10px] text-muted-foreground leading-tight">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Menu Utama */}
      <div className="px-4 sm:px-6 pt-5 pb-2 space-y-4">
        <div className="flex items-center justify-between px-1">
          <h2 className="font-bold text-[16px]">Menu Utama</h2>
        </div>

        {isAdmin ? (
          // Admin / Superadmin: single flat grid, no group labels, larger icons
          <div className="grid grid-cols-4 gap-x-3 gap-y-4">
            {allItems.map((it) => (
              <Link
                key={it.url}
                to={it.url}
                className="flex flex-col items-center gap-1.5 text-center group"
              >
                <div className={cn(
                  "w-14 h-14 rounded-2xl flex items-center justify-center",
                  "shadow-[var(--shadow-elevation-1)] group-active:scale-95 transition-transform",
                  it.color
                )}>
                  <it.icon className="w-7 h-7" />
                </div>
                <span className="text-[10.5px] leading-tight line-clamp-2 text-foreground">
                  {it.title}
                </span>
              </Link>
            ))}
          </div>
        ) : (
          // Guru / Bendahara: 2-column card layout — smaller text, allow wrap
          <div className="grid grid-cols-2 gap-2.5">
            {allItems.map((it) => (
              <Link
                key={it.url}
                to={it.url}
                className="flex items-center gap-2.5 rounded-2xl bg-card border border-border/60 shadow-[var(--shadow-elevation-1)] p-2.5 active:scale-[0.98] transition"
              >
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
                  it.color
                )}>
                  <it.icon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[12px] leading-tight break-words">{it.title}</div>
                  {it.subtitle && (
                    <div className="text-[10px] text-muted-foreground leading-tight mt-0.5 truncate">{it.subtitle}</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
